export const IGN_GEOCODE_URL = 'https://data.geopf.fr/geocodage/search';

export interface IgnGeocodeFeature {
    label: string;
    postcode?: string;
    city?: string;
    departmentCode: number | null;
    longitude: number;
    latitude: number;
}

interface IgnSearchResponse {
    features?: Array<{
        geometry?: { coordinates?: number[] };
        properties?: {
            label?: string;
            postcode?: string;
            city?: string;
            context?: string;
            citycode?: string;
        };
    }>;
}

export const formatCompanyAddress = (
    street?: string | null,
    postalCode?: string | null,
    city?: string | null
): string => {
    return [street, postalCode, city]
        .map((part) => (part || '').trim())
        .filter(Boolean)
        .join(', ');
};

export const departmentCodeFromInseeOrPostcode = (
    postcode?: string | null,
    context?: string | null,
    citycode?: string | null
): number | null => {
    const contextMatch = (context || '').match(/^(\d{2,3}|2A|2B)\b/i);
    if (contextMatch) {
        return departmentCodeFromRaw(contextMatch[1]);
    }
    const cityMatch = (citycode || '').match(/^(\d{2}|2A|2B)/i);
    if (cityMatch) {
        return departmentCodeFromRaw(cityMatch[1]);
    }
    const digits = (postcode || '').replace(/\s/g, '');
    if (/^20\d{3}$/.test(digits)) {
        return 20;
    }
    if (/^97[1-8]\d{2}$/.test(digits) || /^98\d{3}$/.test(digits)) {
        return Number.parseInt(digits.slice(0, 3), 10);
    }
    if (/^\d{5}$/.test(digits)) {
        return Number.parseInt(digits.slice(0, 2), 10);
    }
    return null;
};

export const departmentCodeFromRaw = (raw: string): number | null => {
    const normalized = raw.trim().toUpperCase();
    if (normalized === '2A' || normalized === '2B') {
        return 20;
    }
    const parsed = Number.parseInt(normalized, 10);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 999) {
        return null;
    }
    return parsed;
};

export const parseIgnSearchResponse = (payload: IgnSearchResponse): IgnGeocodeFeature[] => {
    if (!Array.isArray(payload.features)) {
        return [];
    }
    return payload.features
        .map((feature): IgnGeocodeFeature | null => {
            const coords = feature.geometry?.coordinates;
            const longitude = Array.isArray(coords) ? Number(coords[0]) : NaN;
            const latitude = Array.isArray(coords) ? Number(coords[1]) : NaN;
            const properties = feature.properties || {};
            const label = (properties.label || '').trim();
            if (!label || Number.isNaN(longitude) || Number.isNaN(latitude)) {
                return null;
            }
            return {
                label,
                postcode: properties.postcode,
                city: properties.city,
                departmentCode: departmentCodeFromInseeOrPostcode(
                    properties.postcode,
                    properties.context,
                    properties.citycode
                ),
                longitude,
                latitude,
            };
        })
        .filter((item): item is IgnGeocodeFeature => item !== null);
};

export const searchIgnAddresses = async (query: string): Promise<IgnGeocodeFeature[]> => {
    const q = query.trim();
    if (q.length < 3) {
        return [];
    }
    const url = `${IGN_GEOCODE_URL}?q=${encodeURIComponent(q)}&limit=6&index=address`;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`IGN geocode HTTP ${response.status}`);
    }
    const payload = (await response.json()) as IgnSearchResponse;
    return parseIgnSearchResponse(payload);
};

export const toggleDepartmentCode = (codes: number[], code: number): number[] => {
    if (codes.includes(code)) {
        return codes.filter((item) => item !== code);
    }
    return [...codes, code].sort((a, b) => a - b);
};
